/**
 * useDailyUpdates — the React binding for the Daily Updates feed + composer.
 *
 * Strict layering: the page calls THIS hook; the hook calls
 * {@link DailyUpdateStore} (and the existing {@link TaskStore} /
 * {@link WorkstreamStore} / {@link GoalStore} to populate the composer's story/
 * workstream/goal pickers and to resolve their titles CLIENT-SIDE for the
 * feed). It also composes {@link BlockerStore} for the "convert a daily-update
 * blocker into a structured Blocker" path. The stores call the api facade; the
 * page never imports a store or the api directly.
 *
 * The feed is grouped by day → engineer for AWARENESS; there is deliberately no
 * per-engineer count/velocity/ranking.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { TaskStore } from '../tasks/taskStore';
import { WorkstreamStore } from '../workstreams/workstreamStore';
import { GoalStore } from '../goals/goalStore';
import { BlockerStore } from '../blockers/blockerStore';
import { DailyUpdateStore } from './dailyUpdateStore';
import { groupByDayEngineer, type DailyUpdateDayGroup } from './dailyUpdateModel';
import type {
  DailyUpdate,
  DailyUpdateDraft,
  DailyUpdateSummary,
} from './types';
import type { Blocker, BlockerDraft } from '../blockers/types';
import type { Task } from '../tasks/types';
import type { WorkstreamSummary } from '../workstreams/types';
import type { GoalSummary } from '../goals/types';

export interface UseDailyUpdatesResult {
  /** All non-deleted update summaries (newest-first). */
  updates: DailyUpdateSummary[];
  /** Updates grouped by day → engineer for the feed. */
  dayGroups: DailyUpdateDayGroup[];
  /** All tasks (story picker + story-title resolution). */
  tasks: Task[];
  /** Workstream summaries (workstream picker + title resolution). */
  workstreams: WorkstreamSummary[];
  /** Goal summaries (goal picker + title resolution). */
  goals: GoalSummary[];
  /** Current user's alias (composer engineer default). */
  currentAlias: string;
  /** Map of storyId (task id) -> title for feed rendering. */
  storyTitles: Map<string, string>;
  /** Map of workstreamId -> title for feed rendering. */
  workstreamTitles: Map<string, string>;
  /** Map of goalId -> title for feed rendering. */
  goalTitles: Map<string, string>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Posts a new daily update. */
  create: (draft: DailyUpdateDraft) => Promise<DailyUpdate>;
  /** Soft-deletes an update. */
  remove: (id: string) => Promise<void>;
  /**
   * Converts a daily-update blocker into a structured Blocker (the HUMAN-VERIFY
   * → persist step). The draft is pre-filled by the page via
   * blockerModel.draftFromDailyUpdate and confirmed by the human.
   */
  createBlocker: (draft: BlockerDraft) => Promise<Blocker>;
}

export function useDailyUpdates(): UseDailyUpdatesResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      updates: new DailyUpdateStore(api),
      tasks: new TaskStore(api),
      workstreams: new WorkstreamStore(api),
      goals: new GoalStore(api),
      blockers: new BlockerStore(api),
    }),
    [api],
  );

  const [updates, setUpdates] = useState<DailyUpdateSummary[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [workstreams, setWorkstreams] = useState<WorkstreamSummary[]>([]);
  const [goals, setGoals] = useState<GoalSummary[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, allTasks, wsRows, goalRows, identity] = await Promise.all([
        stores.updates.listSummaries(),
        stores.tasks.listAll(),
        stores.workstreams.listSummaries(),
        stores.goals.listSummaries(),
        api.identityProvider.getIdentity(),
      ]);
      setUpdates(rows);
      setTasks(allTasks);
      setWorkstreams(wsRows);
      setGoals(goalRows);
      setCurrentAlias(identity.alias);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load daily updates.');
    } finally {
      setLoading(false);
    }
  }, [stores, api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const dayGroups = useMemo(() => groupByDayEngineer(updates), [updates]);

  const storyTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tasks) m.set(t.id, t.title);
    return m;
  }, [tasks]);

  const workstreamTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of workstreams) m.set(w.id, w.title);
    return m;
  }, [workstreams]);

  const goalTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of goals) m.set(g.id, g.title);
    return m;
  }, [goals]);

  const create = useCallback(
    async (draft: DailyUpdateDraft) => {
      const saved = await stores.updates.create(draft);
      await refresh();
      return saved;
    },
    [stores, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await stores.updates.softDelete(id);
      await refresh();
    },
    [stores, refresh],
  );

  const createBlocker = useCallback(
    (draft: BlockerDraft) => stores.blockers.create(draft),
    [stores],
  );

  return {
    updates,
    dayGroups,
    tasks,
    workstreams,
    goals,
    currentAlias,
    storyTitles,
    workstreamTitles,
    goalTitles,
    loading,
    error,
    refresh,
    create,
    remove,
    createBlocker,
  };
}
